import { defineStore } from "pinia";
import type { TestStep } from "../composables/useInteractiveTest";

export const useInteractiveTestStore = defineStore("interactiveTest", {
  state: () => ({
    isOpen: false,
    isRunning: false,
    isPaused: false,
    title: "",
    steps: [] as TestStep[],
    currentStepIndex: -1,
    speedMs: 800,
    globalLogs: [] as string[],
    startTime: null as number | null,
    totalDurationMs: 0,
  }),

  getters: {
    passedCount: (state) => state.steps.filter((s) => s.status === "passed").length,
    failedCount: (state) => state.steps.filter((s) => s.status === "failed").length,
    isFinished: (state) => state.steps.length > 0 && state.steps.every((s) => s.status === "passed" || s.status === "failed"),
  },

  actions: {
    addLog(msg: string, step?: TestStep) {
      const time = new Date().toLocaleTimeString();
      const formatted = `[${time}] ${msg}`;
      this.globalLogs.push(formatted);
      if (step) step.logs.push(formatted);
    },

    setSuite(title: string, steps: Omit<TestStep, "status" | "logs">[]) {
      this.title = title;
      this.steps = steps.map((s) => ({
        ...s,
        status: "pending",
        logs: [],
      }));
    },

    openTest() {
      this.isOpen = true;
    },

    closeTest() {
      this.isOpen = false;
    },

    resetTest() {
      this.isRunning = false;
      this.isPaused = false;
      this.currentStepIndex = -1;
      this.globalLogs = [];
      this.totalDurationMs = 0;
      this.startTime = null;
      this.steps.forEach((s) => {
        s.status = "pending";
        s.durationMs = undefined;
        s.payload = undefined;
        s.result = undefined;
        s.error = undefined;
        s.logs = [];
      });
    },

    async delay(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async runTest() {
      if (this.isRunning) return;
      this.resetTest();
      this.isRunning = true;
      this.startTime = Date.now();
      this.addLog(`🚀 Starting Test Suite: ${this.title}`);

      for (let i = 0; i < this.steps.length; i++) {
        if (!this.isOpen) break;
        while (this.isPaused) {
          await this.delay(200);
        }
        this.currentStepIndex = i;
        const step = this.steps[i];
        step.status = "running";
        this.addLog(`▶ Step ${i + 1}/${this.steps.length}: ${step.name}`, step);
        await this.delay(this.speedMs);

        const stepStart = Date.now();
        try {
          const res = await step.action();
          step.durationMs = Date.now() - stepStart;
          step.result = res;
          step.status = "passed";
          this.addLog(`✓ PASSED in ${step.durationMs}ms`, step);
        } catch (err: any) {
          step.durationMs = Date.now() - stepStart;
          step.error = err?.message || String(err);
          step.status = "failed";
          this.addLog(`✗ FAILED: ${step.error}`, step);
          this.isRunning = false;
          if (this.startTime) this.totalDurationMs = Date.now() - this.startTime;
          return;
        }
      }

      if (this.startTime) this.totalDurationMs = Date.now() - this.startTime;
      this.isRunning = false;
      this.addLog(`🎉 Test Suite Completed! Passed: ${this.passedCount}/${this.steps.length}`);
    },
  },
});
